import { effect, inject, Injectable, Signal } from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { FormBuilder, Validators } from '@angular/forms';
import { DEFAULT_USA_PHONE_PREFIX } from '@app/core/constants/countries-phone-prefix';
import {
  RelationshipsToMinor,
  RelationshipsToMinorEnum,
} from '@app/minor-estates/shared/enums';
import { FILTER_PATTERNS } from '@shared/constants';
import {
  noFutureDateValidator,
  noOlderThanValidator,
} from '@shared/validators';
import { distinctUntilChanged, map, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class RegisterMinorFormService {
  private readonly _fb = inject(FormBuilder);

  private readonly _affidavitBreakpointAge = 14;
  private readonly _minorBreakpointAge = 18;

  private readonly _form = this._fb.group({
    userId: this._fb.control<number>(0, {
      nonNullable: true,
    }),
    email: this._fb.control('', {
      validators: [Validators.pattern(FILTER_PATTERNS.email)],
    }),
    isFirm: this._fb.control(false, {
      nonNullable: true,
    }),
    firstName: this._fb.control('', {
      validators: [
        Validators.required,
        Validators.minLength(2),
        Validators.pattern(FILTER_PATTERNS.name),
      ],
      nonNullable: true,
    }),
    middleName: this._fb.control('', {
      validators: [Validators.pattern(FILTER_PATTERNS.name)],
    }),
    lastName: this._fb.control('', {
      validators: [
        Validators.required,
        Validators.minLength(2),
        Validators.pattern(FILTER_PATTERNS.name),
      ],
      nonNullable: true,
    }),
    phoneNumber: this._fb.control('', {
      validators: [Validators.pattern(FILTER_PATTERNS.phoneNumberFormatted)],
    }),
    username: this._fb.control('', {
      validators: [Validators.required, Validators.minLength(2)],
    }),
    prefixPhone: this._fb.control<string>(DEFAULT_USA_PHONE_PREFIX),
    courtOrder: this._fb.control<File | null>(null),
    affidavit: this._fb.control<File | null>(null),
    relationshipToMinor: this._fb.control<RelationshipsToMinor | null>(null, {
      validators: [Validators.required],
    }),
    minorFirstName: this._fb.control('', {
      validators: [
        Validators.required,
        Validators.minLength(2),
        Validators.pattern(FILTER_PATTERNS.name),
      ],
      nonNullable: true,
    }),

    minorLastName: this._fb.control('', {
      validators: [
        Validators.required,
        Validators.minLength(2),
        Validators.pattern(FILTER_PATTERNS.name),
      ],
      nonNullable: true,
    }),
    minorDateOfBirth: this._fb.control<string | null>(null, [
      noFutureDateValidator(),
      noOlderThanValidator(this._minorBreakpointAge),
      Validators.required,
    ]),
  });

  public get form() {
    return this._form;
  }

  public is14OrOlder = toSignal(
    this.form.controls.minorDateOfBirth.valueChanges.pipe(
      map((value) => {
        const age = value ? this._calculateAge(value) : false;
        return !!age && age >= this._affidavitBreakpointAge;
      }),
    ),
    {
      initialValue: false,
    },
  );

  public isFirm = toSignal(
    this.form.controls.isFirm.valueChanges.pipe(map((isFirm) => !!isFirm)),
    {
      initialValue: false,
    },
  );

  public readonly isOtherRelationship: Signal<boolean> = toSignal(
    this.form.controls.relationshipToMinor.valueChanges.pipe(
      distinctUntilChanged(),
      map((value) => value === RelationshipsToMinorEnum.Other),
    ),
    {
      initialValue: false,
    },
  );

  private readonly _isOtherRelationship$ = toObservable(
    this.isOtherRelationship,
  ).pipe(
    tap((isOther) => {
      if (isOther) {
        this.form.controls.courtOrder.setValidators([Validators.required]);
      } else {
        this.form.controls.courtOrder.clearValidators();
      }
      this.form.controls.courtOrder.updateValueAndValidity();
    }),
    takeUntilDestroyed(),
  );

  private _calculateAge(dateValue: string): number {
    const birthDate = new Date(dateValue);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }

  private _isFirm$ = this.form.controls.isFirm.valueChanges.pipe(
    tap((isFirm) => {
      if (isFirm) {
        this.form.controls.lastName.disable();
        this.form.controls.middleName.disable();

        this.form.controls.lastName.setValue('');
        this.form.controls.middleName.setValue('');
      } else {
        this.form.controls.lastName.enable();
        this.form.controls.middleName.enable();
        this.form.controls.prefixPhone.enable();
      }
      this.form.updateValueAndValidity();
    }),
    takeUntilDestroyed(),
  );

  constructor() {
    this._isOtherRelationship$.subscribe();

    this._isFirm$.subscribe();

    effect(() => {
      const affidavitFile = this.form.controls.affidavit;
      const isOther = this.isOtherRelationship();
      const is14OrOlder = this.is14OrOlder();

      if (!isOther && is14OrOlder) {
        affidavitFile?.setValidators([Validators.required]);
      } else {
        affidavitFile?.clearValidators();
      }

      affidavitFile?.updateValueAndValidity();
    });
  }
}
